import { Collapse, Divider, IconButton, makeStyles } from '@material-ui/core';
import { mdiChevronDown, mdiChevronUp } from '@mdi/js';
import Icon from '@mdi/react';
import Loading from 'components/Loading/Loading';
import moment from 'moment';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getProjectById } from 'store/projects/services/getProjectById.service';
import { selectProjects } from 'store/projects/selectors/projectsSelectors';
import { getRecordById } from 'store/records/services/getRecordById.service';
import { selectUserActiveEmployerInfo } from 'store/user/selectors.js/user.selectors';
import { getActivityLogIcons } from './activityLogIcons';

const useStyles = makeStyles((theme) => ({
  logRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0),
  },
  logIcons: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    color: theme.palette.text.secondary,
  },
  logTime: {
    color: theme.palette.text.secondary,
    minWidth: 48,
  },
  logAction: {
    flex: 1,
  },
  details: {
    padding: theme.spacing(0.5, 1, 1.5, 7),
    fontSize: '0.85rem',
  },
  detailRow: {
    padding: theme.spacing(0.25, 0),
    color: theme.palette.text.secondary,
  },
  sectionTitle: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
    fontWeight: 600,
  },
  changeRow: {
    padding: theme.spacing(0.25, 0),
  },
  changeOld: {
    color: theme.palette.error.main,
  },
  changeNew: {
    color: theme.palette.success.main,
  },
}));

const formatValue = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

/**
 * One activity-log row. A chevron after the action name expands the row and lazily
 * fetches the target entity by id (project or record) to show its details, plus any
 * metadata.changes (e.g. for EDIT_PROJECT / EDIT_RECORD).
 */
const ActivityLogRow = ({ item }) => {
  const classes = useStyles();
  const { t } = useTranslation('common');
  const activeEmployerInfo = useSelector(selectUserActiveEmployerInfo);
  const projectEntities = useSelector(selectProjects);

  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [fetched, setFetched] = useState(false);

  const { verbIcon, targetIcon } = getActivityLogIcons(item.action);
  const changes = item.metadata && item.metadata.changes;

  const handleToggle = async () => {
    const next = !open;
    setOpen(next);

    if (!next || fetched || !item.targetId) return;
    if (item.targetType !== 'PROJECT' && item.targetType !== 'RECORD') return;

    setLoadingDetail(true);
    try {
      const data =
        item.targetType === 'PROJECT'
          ? await getProjectById(item.targetId, activeEmployerInfo)
          : await getRecordById(item.targetId, activeEmployerInfo);
      setDetail(data || null);
    } catch (e) {
      setDetail(null);
    } finally {
      setLoadingDetail(false);
      setFetched(true);
    }
  };

  const renderDetail = () => {
    if (loadingDetail) return <Loading tiny />;
    if (!detail) return null;

    if (item.targetType === 'PROJECT') {
      return (
        <>
          <div className={classes.detailRow}>
            {t('activity_log.detail_name', 'Name')}: {detail.name || '—'}
          </div>
          <div className={classes.detailRow}>
            {t('activity_log.detail_project_key', 'Project key')}:{' '}
            {detail.projectKey || '—'}
          </div>
        </>
      );
    }

    if (item.targetType === 'RECORD') {
      const recordProjectName =
        (detail.project && projectEntities[detail.project]?.name) ||
        detail.project ||
        '—';

      return (
        <>
          <div className={classes.detailRow}>
            {t('activity_log.detail_clock_in', 'Clock in')}:{' '}
            {detail.clockInAt
              ? moment(detail.clockInAt).format('l HH:mm')
              : '—'}
          </div>
          <div className={classes.detailRow}>
            {t('activity_log.detail_clock_out', 'Clock out')}:{' '}
            {detail.clockOutAt
              ? moment(detail.clockOutAt).format('l HH:mm')
              : '—'}
          </div>
          <div className={classes.detailRow}>
            {t('activity_log.detail_project', 'Project')}: {recordProjectName}
          </div>
          <div className={classes.detailRow}>
            {t('activity_log.detail_assignment', 'Assignment')}:{' '}
            {detail.assignment || '—'}
          </div>
        </>
      );
    }

    return null;
  };

  return (
    <div>
      <div className={classes.logRow}>
        <span className={classes.logIcons}>
          {verbIcon}
          {targetIcon}
        </span>
        <span className={classes.logTime}>
          {moment(item.createdAt).format('HH:mm')}
        </span>
        <span className={classes.logAction}>{item.action}</span>
        <IconButton
          size="small"
          onClick={handleToggle}
          aria-label={t('common.show_more', 'Show more')}
        >
          <Icon path={open ? mdiChevronUp : mdiChevronDown} size={1} />
        </IconButton>
      </div>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <div className={classes.details}>
          {renderDetail()}

          {changes && changes.length > 0 ? (
            <>
              <div className={classes.sectionTitle}>
                {t('activity_log.changes', 'Changes')}
              </div>
              {changes.map((change, index) => (
                <div
                  className={classes.changeRow}
                  key={`${change.field}-${index}`}
                >
                  {change.field}:{' '}
                  <span className={classes.changeOld}>
                    {formatValue(change.old)}
                  </span>{' '}
                  → {' '}
                  <span className={classes.changeNew}>
                    {formatValue(change.new)}
                  </span>
                </div>
              ))}
            </>
          ) : null}
        </div>
      </Collapse>

      <Divider />
    </div>
  );
};

export default ActivityLogRow;
